import { useCallback, useEffect, useRef, useState } from "react";
import { AUTH_API_HOST } from "@/api/apis";
import { User } from "@/models/User";
import { SessionWatch, startSessionWatch } from "@/hooks/auth-session-watch";

/**
 * auth 로그인을 **별도 창으로** 처리하고 완료를 감지한다.
 *
 * 주 표면은 이제 iframe 임베드(`useAuthEmbed`)다. 이 훅이 담당하는 곳은 **임베드 안에서
 * 처리할 수 없는 두 가지**다:
 *   ① **SSO** — 프로바이더가 프레이밍을 거부한다. iframe 이 `bhaptics:embed:sso` 로
 *      요청하면 부모(호출처)가 이 훅으로 창을 연다. **부모가 열어야** `/popup-complete`
 *      의 `window.opener` 가 홈페이지가 되어 빠른 경로(postMessage)가 산다.
 *   ② **임베드 정체** — 프레이밍이 거부되면 iframe 이 빈 박스로 조용히 실패한다.
 *
 * 두 경우 모두 **데스크톱·모바일 공통**이다. 모바일에서 `window.open` 은 새 탭이 되지만,
 * 대안인 전체 리다이렉트는 PDP 옵션을 **항상** 잃는 반면 새 탭은 원 탭이 살아 있는 한
 * 잃지 않는다 — 나쁜 경우가 종전의 보통 경우와 같다. 그래서 표면별 분기를 두지 않는다.
 *
 * 자격증명 입력은 auth 오리진(`/login?mode=popup`)이 담당한다 — 인증 표면을 하나로 유지하는
 * 선택이고, 그래서 OTP 상태머신(재전송·이메일 잠금·수신불가·즉석가입 동의)을 두 벌 만들지 않는다.
 *
 * ## 완료 감지는 `auth-session-watch` 가 공유한다
 * 빠른 경로(postMessage) + 보장 경로(`/api/user` 폴링). 임베드와 **같은 코드**를 쓴다 —
 * 폴링이 한쪽에서만 고쳐지면 조용히 썩는 축이라 공유가 중요하다.
 *
 * ## `popup.closed` 를 종료 근거로 쓰지 않는다
 * 팝업이 Google/Microsoft 로 이동하면 COOP 로 opener 관계가 끊겨 `closed` 가 **즉시 true**
 * 가 된다. 이걸 중단 신호로 쓰면 소셜 로그인이 시작하자마자 감시가 죽어, 사용자가 로그인을
 * 끝내도 본창이 반응하지 않는다.
 *
 * ## 이 훅은 react-query 를 쓰지 않는다
 * `useQueryClient()` 를 쓰면 이 훅을 쓰는 leaf 컴포넌트가 QueryClientProvider 를 요구하게
 * 되고, 실제로 그 결합이 #5293 가드 수트를 통째로 깨뜨렸다. 대신 검증된 `user` 를
 * `onAuthenticated(user)` 로 **올려보내** 호출처가 캐시를 갱신하게 한다.
 */

interface OpenOptions {
  /**
   * 팝업 로그인이 완료된 뒤 실행. 검증된 `user` 를 함께 넘긴다 — 호출처가
   * `setQueryData(["/api/user"], user)` 로 캐시를 맞춰야 "로그인했는데 게스트 화면"
   * (가입 유도 배지·모달 재노출)이 남지 않는다.
   */
  onAuthenticated: (user: User) => void;
  /**
   * 팝업을 쓸 수 없을 때(모바일 · 팝업 차단) 대신 실행. 호출처는 여기서 **기존
   * 리다이렉트 경로**(구성 담기 → auth 이동)를 태워야 한다 — 페이지가 떠나면 PDP 옵션
   * 스토어는 persist 가 꺼져 있어 선택이 날아간다.
   */
  onFallback: () => void;
  /** auth 화면 언어. 홈페이지의 현재 로케일을 그대로 넘긴다. */
  lang?: string;
  /**
   * SSO 딥링크. 실으면 창이 뜨자마자 그 프로바이더로 넘어간다 — 사용자가 같은 버튼을
   * 한 번 더 누르지 않게 한다. auth `/login` 이 고정 맵으로 검증한 뒤 발화한다.
   */
  provider?: string;
}

export function useAuthPopup() {
  const [isWaiting, setIsWaiting] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    setIsWaiting(false);
  }, []);

  // 언마운트 시 정리. 모달이 닫히면 Radix 가 content 를 언마운트하므로 **실전의 취소
  // 경로는 이쪽**이다 — 호출처가 stop() 을 부르는 경로와 별개로 반드시 있어야 한다.
  useEffect(() => () => abortRef.current?.(), []);

  const open = useCallback(
    ({ onAuthenticated, onFallback, lang, provider }: OpenOptions) => {
      stop(); // 재시도 시 이전 감시를 해제하고 isWaiting 까지 되돌린다

      const url = new URL(`${AUTH_API_HOST}/login`);
      url.searchParams.set("mode", "popup");
      url.searchParams.set("success-url", window.location.href);
      if (lang) url.searchParams.set("lang", lang);
      if (provider) url.searchParams.set("provider", provider);

      // 팝업은 클릭 핸들러에서 **동기로** 열어야 차단되지 않는다.
      // `noopener` 를 붙이면 안 된다 — `/popup-complete` 가 `window.opener.postMessage`
      // 로 완료를 알리는 계약이라 opener 가 끊기면 빠른 경로가 죽는다.
      const popup = window.open(
        url.toString(),
        "bhaptics-login-popup",
        "width=480,height=680",
      );
      if (!popup) {
        onFallback(); // 팝업 차단
        return;
      }

      setIsWaiting(true);

      let watch: SessionWatch | null = null;

      const onMessage = (e: MessageEvent) => {
        if (e.origin !== AUTH_API_HOST) return;
        if (e.data?.type === "bhaptics:auth:success") watch?.check();
      };

      watch = startSessionWatch({
        onAuthenticated: (user) => {
          stop();
          onAuthenticated(user);
        },
        // 상한 도달 시 **감시만** 끊는다. 팝업은 사용자 소유로 남긴다 — 메일에서 OTP
        // 코드를 찾는 동안 입력 중인 창이 사라지면 안 된다.
        onTimeout: () => {
          window.removeEventListener("message", onMessage);
          abortRef.current = null;
          setIsWaiting(false);
        },
      });

      window.addEventListener("message", onMessage);

      abortRef.current = () => {
        watch?.stop();
        window.removeEventListener("message", onMessage);
        try {
          if (!popup.closed) popup.close();
        } catch {
          // COOP 로 접근이 막혀도 무해 — 사용자가 직접 닫는다.
        }
      };
    },
    [stop],
  );

  return { open, stop, isWaiting };
}
